import { useState, useEffect } from "react";
import axios from "axios";
import { useToast } from "../admincomponents/AdminToast";
import { BASE_URL } from "../baseurl";

export default function Orders() {
  const toast = useToast();
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken"); 

      const res = await axios.get(
        `${BASE_URL}/purchase/purchases`, 
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      
      const mapped = res.data.purchases.map((p) => ({
        id: p._id,
        customer: {
          name: p.user?.username || "Unknown",
          email: p.user?.email || "—",
        },
        date: new Date(p.createdAt).toISOString().slice(0, 10),
        total: p.totalPrice,
        payment: p.paymentMethod || "—",
        shippingAddress: p.shippingAddress || null,
        agreements: p.agreements || {},
        items: (p.items || []).map((item) => ({
          name: item.title,
          qty: item.quantity || 1,
          price: item.price,
        })),
      }));

      setOrders(mapped);
    } catch (error) {
      console.error("Fetch purchases error:", error);
      toast({
        variant: "error",
        title: "Couldn't load orders",
        description:
          error.response?.data?.message || "Something went wrong fetching purchases.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrders = orders.filter((order) => {
    return (
      order.id.toLowerCase().includes(search.toLowerCase()) ||
      order.customer.name.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <div className="w-full">
      <div className="mb-8">
        <h1 className="text-gray-800 font-semibold text-2xl">Orders</h1>
        <p className="text-gray-500 text-sm mt-1">
          Manage and track customer orders
        </p>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.5" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search by order ID or customer..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-white border border-gray-300 rounded-md pl-9 pr-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:border-teal-500"
          />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left">
                <th className="px-6 py-4 text-gray-500 font-medium">Order</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Customer</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Date</th>
                <th className="px-6 py-4 text-gray-500 font-medium">Total</th>
                <th className="px-6 py-4 text-gray-500 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    Loading orders...
                  </td>
                </tr>
              )}

              {!isLoading &&
                filteredOrders.map((order) => (
                  <tr
                    key={order.id}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 text-gray-800 font-medium">{order.id}</td>
                    <td className="px-6 py-4">
                      <p className="text-gray-800">{order.customer.name}</p>
                      <p className="text-gray-400 text-xs">{order.customer.email}</p>
                    </td>
                    <td className="px-6 py-4 text-gray-600">{order.date}</td>
                    <td className="px-6 py-4 text-gray-800 font-medium">
                      ${order.total.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="text-teal-600 hover:text-teal-700 font-medium text-sm"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}

              {!isLoading && filteredOrders.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-gray-400">
                    No orders match your search.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selectedOrder && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setSelectedOrder(null)}
          />

          <div className="fixed top-0 right-0 h-full w-full max-w-[480px] bg-white z-50 flex flex-col shadow-xl">
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
              <div>
                <h2 className="text-gray-800 font-semibold text-lg">
                  Order {selectedOrder.id}
                </h2>
                <p className="text-gray-400 text-xs mt-0.5">{selectedOrder.date}</p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                className="text-gray-400 hover:text-gray-700"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              <div className="mb-6">
                <h3 className="text-gray-700 font-semibold text-sm mb-3">Customer</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="text-gray-800 text-sm font-medium">
                    {selectedOrder.customer.name}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">
                    {selectedOrder.customer.email}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-gray-700 font-semibold text-sm mb-3">
                  Shipping Address
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  {selectedOrder.shippingAddress ? (
                    <>
                      <p className="text-gray-800 text-sm font-medium">
                        {selectedOrder.shippingAddress.firstName}{" "}
                        {selectedOrder.shippingAddress.lastName}
                      </p>
                      <p className="text-gray-600 text-sm mt-1">
                        {selectedOrder.shippingAddress.address}
                        {selectedOrder.shippingAddress.apartment
                          ? `, ${selectedOrder.shippingAddress.apartment}`
                          : ""}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {selectedOrder.shippingAddress.city},{" "}
                        {selectedOrder.shippingAddress.postcode}
                      </p>
                      <p className="text-gray-600 text-sm">
                        {selectedOrder.shippingAddress.state},{" "}
                        {selectedOrder.shippingAddress.country}
                      </p>
                      {selectedOrder.shippingAddress.phone && (
                        <p className="text-gray-500 text-xs mt-2">
                          Phone: {selectedOrder.shippingAddress.phone}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-400 text-sm">No address on file.</p>
                  )}
                </div>
              </div>

              {/* <div className="mb-6">
                <h3 className="text-gray-700 font-semibold text-sm mb-3">Payment</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                  <span className="text-gray-600 text-sm">Method</span>
                  <span className="text-gray-800 text-sm font-medium">
                    {selectedOrder.payment}
                  </span>
                </div>
              </div> */}

              <div className="mb-6">
                <h3 className="text-gray-700 font-semibold text-sm mb-3">Agreements</h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Terms Accepted</span>
                    <span
                      className={`text-sm font-medium ${
                        selectedOrder.agreements?.terms ? "text-teal-600" : "text-red-500"
                      }`}
                    >
                      {selectedOrder.agreements?.terms ? "Yes" : "No"}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 text-sm">Research Only Acknowledged</span>
                    <span
                      className={`text-sm font-medium ${
                        selectedOrder.agreements?.researchOnly ? "text-teal-600" : "text-red-500"
                      }`}
                    >
                      {selectedOrder.agreements?.researchOnly ? "Yes" : "No"}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-gray-700 font-semibold text-sm mb-3">Items</h3>
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-gray-800 text-sm">{item.name}</p>
                        <p className="text-gray-400 text-xs">Qty: {item.qty}</p>
                      </div>
                      <p className="text-gray-800 text-sm font-medium">
                        ${(item.price * item.qty).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 px-6 py-5">
              <div className="flex items-center justify-between">
                <span className="text-gray-600 font-medium text-sm">Total</span>
                <span className="text-gray-800 font-semibold text-lg">
                  ${selectedOrder.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}